"use client";

/**
 * The Overview section — the top of the report document.
 *
 * Extracted from the design source `compass-oppreport.jsx` (the `overview`
 * section: `.rv-hdrbadges`, `.rv-headline`, `.rv-standfirst`,
 * `.rv-grouphead`, `.rv-eyebrow`, `.rv-tiles`, `.rv-pending`).
 *
 * Design order, top to bottom: the scope header, the problem block, "Key
 * figures" with its 4-up tiles, "Metrics" with its 4-up tiles, and the pending
 * notice.
 *
 * The problem block is the design's `.rv-headline` / `.rv-standfirst` pair —
 * display-size statement over supporting prose — not an eyebrow over body
 * copy. The design's group headings carry no "View all →" either; the tiles
 * are the navigation, and `onViewFigures` / `onViewMetrics` still ride on
 * them. Only `onOpenAssumptions` opens the assumptions tab.
 *
 * The scope and product badges open the section because the design retired
 * its standalone Scope section and moved deployment scope into the Overview
 * header. The Highlights / Key risks split that used to close it is gone:
 * risks have their own `risks` section now (`ReportKeyRisks`), and the
 * design has no Highlights surface at all.
 */

import type { ProductIdentity } from "./product-identity";
import { ReportCitationMarker } from "./ReportCitationMarker";
import { ReportHeaderBadges } from "./ReportHeaderBadges";
import { ReportPendingNotice } from "./ReportPendingNotice";
import { ReportSectionHeading } from "./ReportSectionHeading";
import { ReportTile, ReportTileGrid } from "./ReportTile";
import { buildReportFigureRows } from "./report-figures";
import { formatHorizonYears, REPORT_EMPTY_VALUE } from "./report-format";
import type {
  OpenAssumptionsHandler,
  ReportFigures,
  ReportMetricRow,
  ReportProblem,
  ReportScope,
} from "./report-types";

export interface ReportOverviewProps {
  problem: ReportProblem;
  scope: ReportScope;
  /** The product under evaluation, for the header badge. `null` ⇒ placeholder. */
  product: ProductIdentity | null;
  figures: ReportFigures;
  metrics: ReportMetricRow[];
  needsInputCount: number | null;
  onViewFigures?: () => void;
  onViewMetrics?: () => void;
  onViewProduct?: () => void;
  onOpenCitation?: (n: number) => void;
  onOpenAssumptions?: OpenAssumptionsHandler;
}

/**
 * The design's Overview opens with a one-sentence `.rv-headline` over a
 * supporting `.rv-standfirst`. Real briefs put the whole brief in
 * `problem.statement` and leave `problem.detail` null (§5 gap #6), and a
 * three-sentence paragraph set at 30px is a wall rather than a headline.
 *
 * Splitting on the first sentence boundary fills both slots from copy that
 * already exists — nothing is invented, and nothing is dropped. A statement
 * that is already one sentence, or that has an explicit `detail`, passes
 * through untouched. The trailing text must start with a capital or a digit
 * so "1.5 hours" and most abbreviations do not read as a sentence end.
 */
function splitProblemProse(
  statement: string,
  detail: string | null,
): { headline: string; standfirst: string | null } {
  if (detail) return { headline: statement, standfirst: detail };
  const match = /^(.+?[.!?])\s+([A-Z0-9].*)$/s.exec(statement.trim());
  const headline = match?.[1];
  const standfirst = match?.[2];
  if (!headline || !standfirst)
    return { headline: statement, standfirst: null };
  return { headline, standfirst };
}

/**
 * The Overview section body. Every figure and metric renders `—` while
 * `figures.ready` is false — the normal uncomputed state, not an error
 * (plan §6 risk 2).
 */
export function ReportOverview({
  problem,
  scope,
  product,
  figures,
  metrics,
  needsInputCount,
  onViewFigures,
  onViewMetrics,
  onViewProduct,
  onOpenCitation,
  onOpenAssumptions,
}: ReportOverviewProps) {
  const figureRows = buildReportFigureRows(figures);
  const horizonLabel = formatHorizonYears(figures.horizonYears);
  // Same gate as `reportLocked` in the app (Intake.tsx) and `CS.missing()`
  // in the design: an unknown count is treated as incomplete.
  const locked = needsInputCount === null || needsInputCount > 0;
  // `null` or blank ⇒ no opportunity brief has been generated yet. This is
  // distinct from §5 gap #6 (`problem.detail`, a field with no backing data
  // at all) — a statement normally exists once a brief exists, so this is a
  // "not generated yet" state rather than a permanent gap.
  const statement = problem.statement?.trim();
  const prose = statement ? splitProblemProse(statement, problem.detail) : null;

  return (
    <div className="font-[family-name:var(--font-body)]">
      {/* ---- header badges ---- `.rv-hdrbadges`, above the headline. */}
      <ReportHeaderBadges
        scope={scope}
        product={product}
        onViewProduct={onViewProduct}
      />

      {/* ---- problem ---- */}
      {prose ? (
        <>
          {/* `.rv-headline` — the document's one h1. The `!` is load-bearing:
              `tokens.css` styles bare `h1`/`h2` outside any cascade layer
              (`h1 { font-size: 17px }`), and an unlayered element selector
              beats every Tailwind utility, which all live in
              `@layer utilities`. Without it this renders at 17px. */}
          <h1 className="m-0 mb-3 text-pretty! text-[30px]! font-bold leading-[1.22]! tracking-[-0.02em]! text-(--fg-primary)">
            {prose.headline}
            {problem.citation != null && (
              <ReportCitationMarker
                n={problem.citation}
                onClick={onOpenCitation}
              />
            )}
          </h1>
          {/* `.rv-standfirst` */}
          {prose.standfirst && (
            <p className="m-0 mb-[30px] max-w-[64ch] text-[15px] leading-[1.6] text-(--fg-secondary)">
              {prose.standfirst}
            </p>
          )}
        </>
      ) : (
        // The problem block is a lede, not a card — keep this modest (muted,
        // body-copy size) rather than styling it like a headline gap the way
        // `ReportComparableVendors` boxes off a missing section with a
        // dashed card.
        <p className="m-0 mb-4 text-[13px] leading-[1.6] text-(--fg-muted)">
          No opportunity brief has been generated for this opportunity yet, so
          there is no problem statement to show here.
        </p>
      )}

      {/* ---- key figures ---- */}
      <ReportSectionHeading title="Key figures" level="group" />
      <ReportTileGrid columns={4}>
        {figureRows.map((row) => (
          <ReportTile
            key={row.key}
            label={row.label}
            value={row.value}
            assumed={row.horizonAssumed}
            horizonLabel={horizonLabel}
            onClick={onViewFigures}
          />
        ))}
      </ReportTileGrid>

      {/* ---- metrics ---- */}
      <ReportSectionHeading title="Metrics" level="group" />
      <ReportTileGrid columns={4}>
        {metrics.map((metric) => (
          <ReportTile
            key={metric.id}
            label={metric.label}
            value={figures.ready ? metric.amount : REPORT_EMPTY_VALUE}
            valueSuffix={
              metric.cadence === "annual"
                ? "per year"
                : metric.cadence === "one-time-with-rollout"
                  ? "as rollout progresses"
                  : "one time"
            }
            // §5 gap #5 — no backing field for the baseline → projected shift
            // on real data, so most rows render without one.
            shift={figures.ready ? (metric.shift ?? undefined) : undefined}
            onClick={onViewMetrics}
          />
        ))}
      </ReportTileGrid>

      {/* ---- pending notice ---- */}
      {locked && (
        <div className="mt-[22px]">
          <ReportPendingNotice
            count={needsInputCount ?? 0}
            onOpenAssumptions={onOpenAssumptions}
          />
        </div>
      )}
    </div>
  );
}
